import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../utils/api';
import { useI18n } from '../../../hooks/useI18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeftIcon, SaveIcon } from 'lucide-react';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";

interface MetricDimension {
  id: string;
  dimension_code: string;
  display_name: string;
}

interface MetricFormData {
  metric_code: string;
  display_name: string;
  description: string;
  category: string;
  metric_type: string;
  aggregation: string;
  source_table: string;
  source_field: string;
  value_type: string;
  decimal_places: number;
  supported_dimensions: string[];
}

// These will be wrapped with translation functions in the component
const METRIC_TYPES_KEYS = [
  { value: 'simple', labelKey: 'metrics.simpleAggregation' },
  { value: 'ratio', labelKey: 'metrics.ratioComingSoon', disabled: true },
  { value: 'derived', labelKey: 'metrics.derivedComingSoon', disabled: true },
  { value: 'custom_sql', labelKey: 'metrics.customSqlComingSoon', disabled: true },
];

const AGGREGATIONS_KEYS = [
  { value: 'sum', labelKey: 'metrics.sum' },
  { value: 'count', labelKey: 'metrics.count' },
  { value: 'avg', labelKey: 'metrics.average' },
  { value: 'min', labelKey: 'metrics.minimum' },
  { value: 'max', labelKey: 'metrics.maximum' },
];

const VALUE_TYPES_KEYS = [
  { value: 'currency', labelKey: 'metrics.currency' },
  { value: 'percentage', labelKey: 'metrics.percentage' },
  { value: 'count', labelKey: 'metrics.count' },
  { value: 'decimal', labelKey: 'metrics.decimal' },
];

const SOURCE_TABLES_KEYS = [
  { value: 'orders', labelKey: 'metrics.ordersSource' },
  { value: 'invoices_ap', labelKey: 'metrics.invoicesAPSource' },
  { value: 'invoices_ar', labelKey: 'metrics.invoicesARSource' },
  { value: 'payments', labelKey: 'metrics.paymentsSource' },
  { value: 'inventory_transactions', labelKey: 'metrics.inventoryTransactionsSource' },
  { value: 'timesheet_shifts', labelKey: 'metrics.timesheetShiftsSource' },
  { value: 'timesheet_activities', labelKey: 'metrics.timesheetActivitiesSource' },
];

const CATEGORIES_KEYS = [
  { value: 'revenue', labelKey: 'metrics.categories.revenue' },
  { value: 'cost', labelKey: 'metrics.categories.cost' },
  { value: 'traffic', labelKey: 'metrics.categories.traffic' },
  { value: 'inventory', labelKey: 'metrics.categories.inventory' },
  { value: 'people', labelKey: 'metrics.categories.people' },
  { value: 'other', labelKey: 'metrics.categories.other' },
];

export default function MetricEditorPage() {
  const navigate = useNavigate();
  const params = useParams({ from: '/metrics/$id' });
  const { t } = useI18n();

  const { id } = params;
  const isNew = id === 'new';

  const [dimensions, setDimensions] = useState<MetricDimension[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<MetricFormData>({
    metric_code: '',
    display_name: '',
    description: '',
    category: 'revenue',
    metric_type: 'simple',
    aggregation: 'sum',
    source_table: 'orders',
    source_field: '',
    value_type: 'currency',
    decimal_places: 2,
    supported_dimensions: [],
  });

  useEffect(() => {
    fetchDimensions();
    if (!isNew) {
      fetchMetric();
    }
  }, [id]);

  const fetchDimensions = async () => {
    try {
      const response = await apiFetch('/api/metrics/dimensions');

      if (!response.ok) {
        throw new Error('Failed to fetch dimensions');
      }

      const data = await response.json();
      setDimensions(data);
    } catch (err) {
      console.error('Failed to fetch dimensions:', err);
    }
  };

  const fetchMetric = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`/api/metrics/definitions/${id}`);

      if (!response.ok) {
        throw new Error('Failed to fetch metric');
      }

      const data = await response.json();
      setFormData({
        metric_code: data.metric_code,
        display_name: data.display_name,
        description: data.description || '',
        category: data.category || 'revenue',
        metric_type: data.metric_type,
        aggregation: data.aggregation || 'sum',
        source_table: data.source_table || 'orders',
        source_field: data.source_field || '',
        value_type: data.value_type,
        decimal_places: data.decimal_places || 2,
        supported_dimensions: data.supported_dimensions || [],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const url = isNew
        ? '/api/metrics/definitions'
        : `/api/metrics/definitions/${id}`;

      const method = isNew ? 'POST' : 'PATCH';

      const response = await apiFetch(url, {
        method,
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save metric');
      }

      const data = await response.json();
      setSuccess(true);

      if (isNew) {
        // Navigate to the new metric
        setTimeout(() => {
          navigate({ to: `/metrics/${data.id}` });
        }, 1000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const toggleDimension = (dimensionCode: string) => {
    setFormData(prev => ({
      ...prev,
      supported_dimensions: prev.supported_dimensions.includes(dimensionCode)
        ? prev.supported_dimensions.filter(d => d !== dimensionCode)
        : [...prev.supported_dimensions, dimensionCode],
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <p className="text-center text-gray-500">{t('actions.loading', 'common')}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: '/metrics' })}
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">
              {isNew ? t('metrics.newMetricTitle', 'common') : t('metrics.editMetricTitle', 'common')}
            </h1>
            <p className="text-gray-600 mt-1">
              {isNew ? t('metrics.createMetricDescription', 'common') : t('metrics.updateMetricDescription', 'common')}
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2"
          >
            <SaveIcon className="h-4 w-4" />
            {saving ? t('metrics.saving', 'common') : t('actions.save', 'common')}
          </Button>
        </div>

        {/* Success */}
        {success && (
          <Alert>
            <AlertDescription>{t('metrics.saveSuccess', 'common')}</AlertDescription>
          </Alert>
        )}

        {/* Error */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>{t('metrics.basicInformation', 'common')}</CardTitle>
            <CardDescription>{t('metrics.basicInformationDescription', 'common')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="metric_code">{t('metrics.metricCode', 'common')} *</Label>
                <Input
                  id="metric_code"
                  value={formData.metric_code}
                  onChange={(e) => setFormData({ ...formData, metric_code: e.target.value })}
                  placeholder={t('metrics.metricCodePlaceholder', 'common')}
                />
                <p className="text-xs text-gray-500 mt-1">{t('metrics.metricCodeHint', 'common')}</p>
              </div>

              <div>
                <Label htmlFor="display_name">{t('metrics.displayName', 'common')} *</Label>
                <Input
                  id="display_name"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  placeholder={t('metrics.displayNamePlaceholder', 'common')}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">{t('forms.description', 'common')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('metrics.descriptionPlaceholder', 'common')}
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="category">{t('forms.category', 'common')}</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES_KEYS.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {t(cat.labelKey, 'common')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Calculation Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>{t('metrics.calculation', 'common')}</CardTitle>
            <CardDescription>{t('metrics.calculationDescription', 'common')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="metric_type">{t('metrics.metricTypeLabel', 'common')}</Label>
                <Select
                  value={formData.metric_type}
                  onValueChange={(value) => setFormData({ ...formData, metric_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {METRIC_TYPES_KEYS.map((type) => (
                      <SelectItem
                        key={type.value}
                        value={type.value}
                        disabled={type.disabled}
                      >
                        {t(type.labelKey, 'common')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="aggregation">{t('metrics.aggregation', 'common')}</Label>
                <Select
                  value={formData.aggregation}
                  onValueChange={(value) => setFormData({ ...formData, aggregation: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGGREGATIONS_KEYS.map((agg) => (
                      <SelectItem key={agg.value} value={agg.value}>
                        {t(agg.labelKey, 'common')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="source_table">{t('metrics.sourceTable', 'common')}</Label>
                {(() => {
                  // Compute UI value for invoices based on category so AP/AR appear distinct
                  const uiSourceValue =
                    formData.source_table === 'invoices'
                      ? (formData.category === 'cost' ? 'invoices_ap' : 'invoices_ar')
                      : formData.source_table;
                  return (
                    <Select
                      value={uiSourceValue}
                      onValueChange={(value) => {
                        if (value === 'invoices_ap') {
                          setFormData({ ...formData, source_table: 'invoices', category: 'cost' })
                        } else if (value === 'invoices_ar') {
                          setFormData({ ...formData, source_table: 'invoices', category: 'revenue' })
                        } else {
                          setFormData({ ...formData, source_table: value })
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SOURCE_TABLES_KEYS.map((table) => (
                          <SelectItem key={table.value} value={table.value}>
                            {t(table.labelKey, 'common')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )
                })()}
              </div>

              <div>
                <Label htmlFor="source_field">{t('metrics.sourceField', 'common')} *</Label>
                <Input
                  id="source_field"
                  value={formData.source_field}
                  onChange={(e) => setFormData({ ...formData, source_field: e.target.value })}
                  placeholder={t('metrics.sourceFieldPlaceholder', 'common')}
                />
                <p className="text-xs text-gray-500 mt-1">{t('metrics.sourceFieldHint', 'common')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Display Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>{t('metrics.display', 'common')}</CardTitle>
            <CardDescription>{t('metrics.displayDescription', 'common')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="value_type">{t('metrics.valueType', 'common')}</Label>
                <Select
                  value={formData.value_type}
                  onValueChange={(value) => setFormData({ ...formData, value_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VALUE_TYPES_KEYS.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {t(type.labelKey, 'common')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="decimal_places">{t('metrics.decimalPlaces', 'common')}</Label>
                <Input
                  id="decimal_places"
                  type="number"
                  min="0"
                  max="4"
                  value={formData.decimal_places}
                  onChange={(e) => setFormData({ ...formData, decimal_places: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dimensions */}
        <Card>
          <CardHeader>
            <CardTitle>{t('metrics.supportedDimensions', 'common')}</CardTitle>
            <CardDescription>{t('metrics.supportedDimensionsDescription', 'common')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dimensions
                .filter((dim) => {
                  // Hide Sales product category on invoice metrics; hide Cost category on order metrics
                  if (formData.source_table === 'invoices' && dim.dimension_code === 'revenue_category') return false;
                  if ((formData.source_table === 'orders' || formData.source_table === 'order_items') && dim.dimension_code === 'cost_category') return false;
                  return true;
                })
                .map((dim) => (
                  <div key={dim.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={dim.dimension_code}
                      checked={formData.supported_dimensions.includes(dim.dimension_code)}
                      onCheckedChange={() => toggleDimension(dim.dimension_code)}
                    />
                    <label
                      htmlFor={dim.dimension_code}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {dim.display_name}
                    </label>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

export const Route = createFileRoute('/metrics/$id')({
  component: MetricEditorPage,
})
